// Punto único de decisión del proveedor del mapa, con carga dinámica.
//
// Orden de preferencia (2026-09):
//   1. **Google Maps** si hay `VITE_GOOGLE_MAPS_KEY` + `VITE_GOOGLE_MAP_ID` o clave manual
//      guardada en localStorage. Requiere billing en Cloud Console; sólo lo uso si el operador
//      lo activa explícitamente. Si el SDK falla al validar la clave (`gm_authFailure`),
//      caemos al siguiente nivel.
//   2. **MapLibre GL + OpenFreeMap** (positron): gratis, sin auth y sin cuota conocida. Es el
//      default para publicación en Play Store — cero licencias, cero costes.
//   3. **Leaflet + CARTO Voyager** como último recurso si MapLibre no arranca (raro, pero
//      posible con navegadores sin WebGL 2).
//
// Cada implementación es un módulo aparte y se carga con `import()` dinámico: la app inicial
// solo baja los KB del proveedor que se va a usar.

import type { MapAdapter } from "./types";
import { persistGetString, persistSetString, persistRemove } from "../persist";

const KEY_STORAGE = "buselx_gkey";

export const getGoogleKey = (): string =>
  persistGetString(KEY_STORAGE, "") || (import.meta.env.VITE_GOOGLE_MAPS_KEY ?? "");

export const setGoogleKey = (value: string): void => {
  if (value.trim() === "") persistRemove(KEY_STORAGE);
  else persistSetString(KEY_STORAGE, value.trim());
};

export const getGoogleMapId = (): string => import.meta.env.VITE_GOOGLE_MAP_ID ?? "";

// Handler global de `gm_authFailure`. El SDK de Google lo invoca cuando la clave es inválida,
// no está autorizada para este referrer, o el proyecto tiene facturación suspendida.
let authFailureHandler: (() => void) | null = null;
export function onGoogleAuthFailure(handler: () => void): void {
  authFailureHandler = handler;
}

if (typeof window !== "undefined") {
  (window as unknown as { gm_authFailure?: () => void }).gm_authFailure = () => {
    console.warn("Google Maps: gm_authFailure — clave rechazada.");
    authFailureHandler?.();
  };
}

export type MapProvider = "google" | "maplibre" | "leaflet";

export interface LoadedMap {
  adapter: MapAdapter;
  provider: MapProvider;
}

export async function loadMap(): Promise<LoadedMap> {
  const key = getGoogleKey();
  const mapId = getGoogleMapId();

  if (key && mapId) {
    try {
      const mod = await import("./google");
      const adapter = await mod.createGoogleAdapter({ key, mapId });
      return { adapter, provider: "google" };
    } catch (err) {
      console.warn("Google Maps no disponible, uso MapLibre:", err);
    }
  }

  try {
    const mod = await import("./maplibre");
    return { adapter: mod.createMapLibreAdapter(), provider: "maplibre" };
  } catch (err) {
    console.warn("MapLibre no disponible, uso Leaflet:", err);
  }

  const mod = await import("./leaflet");
  return { adapter: mod.createLeafletAdapter(), provider: "leaflet" };
}
